/* Hallmark · component-scope: session-and-material-editor · theme: Operational Architect · pre-emit critique: P5 H5 E4 S5 R5 V5 · contrast: pass (40–41) · tokens: pass (48) · responsive: pass (34,49) */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@lms/ui';
import {
  CheckCircle2,
  ClipboardCheck,
  Clock,
  ExternalLink,
  FileText,
  Globe,
  LayoutList,
  Loader2,
  Pencil,
  Plus,
  Video,
  X,
} from 'lucide-react';
import { useCreateSession, useUpdateSession } from '../hooks/useLessonSessions';
import type { LessonSession, Material } from '@lms/shared/types';
import { useLanguage, type Bilingual as BilingualType } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { MaterialModal } from './MaterialModal';

interface SessionModalProps {
  programId: string;
  subjectId: string;
  subjectName?: string;
  sessionNumber: number;
  session?: LessonSession;
  onClose: () => void;
}

type SessionTab = 'DETAILS' | 'LIVE' | 'MATERIALS' | 'REVIEW';

export function SessionModal({
  programId,
  subjectId,
  subjectName,
  sessionNumber: defaultSessionNumber,
  session,
  onClose,
}: SessionModalProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SessionTab>('DETAILS');
  const [managedSession, setManagedSession] = useState<LessonSession | undefined>(session);
  const [sessionNumber, setSessionNumber] = useState(
    session?.sessionNumber ?? defaultSessionNumber
  );
  const [title, setTitle] = useState(
    session?.title || `Pertemuan ${session?.sessionNumber ?? defaultSessionNumber}`
  );
  const [duration, setDuration] = useState(session?.duration ?? 30);
  const [description, setDescription] = useState(session?.description || '');
  const [meetingUrl, setMeetingUrl] = useState(session?.meetingUrl || '');
  const [editingMaterial, setEditingMaterial] = useState<Material | undefined>();
  const [isMaterialEditorOpen, setIsMaterialEditorOpen] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const createSessionMutation = useCreateSession();
  const updateSessionMutation = useUpdateSession();

  const isPending = createSessionMutation.isPending || updateSessionMutation.isPending;
  const isPersisted = Boolean(managedSession);
  const materials = useMemo(
    () => [...(managedSession?.materials || [])].sort((left, right) => left.order - right.order),
    [managedSession?.materials]
  );

  const meetingPlatform = useMemo(() => {
    if (!meetingUrl) return null;
    const url = meetingUrl.toLowerCase();
    if (url.includes('meet.google.com')) return 'Google Meet';
    if (url.includes('zoom.us')) return 'Zoom';
    if (url.includes('teams.microsoft.com')) return 'Microsoft Teams';
    return t(copy.sessionModal.genericLiveLink);
  }, [meetingUrl, t]);

  const sessionData = {
    sessionNumber: Number(sessionNumber) || 1,
    title: title.trim(),
    duration: Number(duration) || undefined,
    description: description.trim() || undefined,
    meetingUrl: meetingUrl.trim() || undefined,
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;

    if (managedSession) {
      updateSessionMutation.mutate(
        { id: managedSession.id, programId, data: sessionData },
        {
          onSuccess: updated => {
            setManagedSession(current => ({ ...current, ...updated }));
            setSavedNotice(true);
          },
        }
      );
      return;
    }

    createSessionMutation.mutate(
      { programId, data: { subjectId, ...sessionData } },
      {
        onSuccess: created => {
          setManagedSession(created);
          setActiveTab('MATERIALS');
          setSavedNotice(true);
        },
      }
    );
  };

  const handleMaterialSaved = (savedMaterial: Material) => {
    setManagedSession(current => {
      if (!current) return current;
      const exists = current.materials?.some(material => material.id === savedMaterial.id);
      const materials = exists
        ? (current.materials || []).map(material =>
            material.id === savedMaterial.id ? { ...material, ...savedMaterial } : material
          )
        : [...(current.materials || []), savedMaterial];
      return { ...current, materials };
    });
    setSavedNotice(true);
  };

  const handleMaterialDeleted = (materialId: string) => {
    setManagedSession(current => {
      if (!current) return current;
      return {
        ...current,
        materials: (current.materials || []).filter(material => material.id !== materialId),
      };
    });
    setSavedNotice(true);
  };

  const tabs: { id: SessionTab; label: BilingualType; icon: React.ReactNode }[] = [
    { id: 'DETAILS', label: copy.sessionModal.tabDetails, icon: <LayoutList size={15} /> },
    { id: 'LIVE', label: copy.sessionModal.tabLive, icon: <Globe size={15} /> },
    { id: 'MATERIALS', label: copy.sessionModal.tabMaterial, icon: <Video size={15} /> },
    { id: 'REVIEW', label: copy.sessionModal.tabReview, icon: <CheckCircle2 size={15} /> },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center overflow-y-auto bg-on-surface/40 p-3 backdrop-blur-sm animate-fadeIn sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-editor-title"
      onKeyDown={event => {
        if (event.key === 'Escape') onClose();
      }}
      tabIndex={-1}
    >
      <div className="my-auto flex max-h-[calc(100dvh-1.5rem)] w-full max-w-4xl flex-col overflow-hidden rounded-[2rem] bg-surface-container-lowest shadow-level5 animate-slideUp sm:max-h-[calc(100dvh-3rem)]">
        <header className="flex items-start justify-between gap-4 bg-surface-container-low px-5 py-5 sm:px-8 sm:py-6">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                <Bilingual
                  {...(isPersisted ? copy.sessionModal.modeEdit : copy.sessionModal.modeCreate)}
                />
              </span>
              <span className="max-w-full truncate text-[11px] font-body font-medium text-on-surface-variant">
                {subjectName || t(copy.sessionModal.subjectFallback)} ·{' '}
                <Bilingual {...copy.sessionModal.sessionLabel} /> {sessionNumber}
              </span>
            </div>
            <h2
              id="session-editor-title"
              className="mt-3 font-headline text-2xl font-bold tracking-tight text-on-surface sm:text-3xl"
            >
              <Bilingual
                {...(isPersisted ? copy.sessionModal.titleEdit : copy.sessionModal.titleCreate)}
              />
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.sessionModal.editorDescription} />
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t(copy.sessionModal.ariaCloseEditor)}
            className="grid size-11 shrink-0 place-items-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-highest hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            <X size={20} />
          </button>
        </header>

        <nav
          className="flex gap-1 overflow-x-auto bg-surface-container-low px-4 pb-3 sm:px-8"
          aria-label={t(copy.sessionModal.editorNavigation)}
        >
          {tabs.map(tab => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-current={isActive ? 'step' : undefined}
                className={`inline-flex h-11 shrink-0 items-center gap-2 rounded-xl px-3.5 text-[11px] font-label font-bold whitespace-nowrap transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${isActive ? 'bg-surface-container-lowest text-primary shadow-level1' : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface'}`}
              >
                {tab.icon}
                <Bilingual {...tab.label} />
                {tab.id === 'MATERIALS' && isPersisted && (
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] text-primary">
                    {materials.length}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
            {savedNotice && (
              <div
                role="status"
                className="mb-6 flex items-center gap-2 rounded-xl bg-success-container px-4 py-3 text-xs font-label font-bold text-on-success-container"
              >
                <CheckCircle2 size={16} />
                <Bilingual {...copy.sessionModal.saveSuccess} />
              </div>
            )}

            {activeTab === 'DETAILS' && (
              <section className="mx-auto max-w-3xl space-y-8">
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.sessionModal.stepOne} />
                  </p>
                  <h3 className="mt-2 font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.sessionModal.detailsHeading} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.sessionModal.detailsDescription} />
                  </p>
                </div>
                <div className="grid gap-5 sm:grid-cols-[minmax(0,0.35fr)_minmax(0,1fr)]">
                  <div className="space-y-2">
                    <label
                      htmlFor="session-number"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.sessionModal.labelSessionNumber} />
                    </label>
                    <input
                      id="session-number"
                      type="number"
                      min="1"
                      required
                      value={sessionNumber}
                      onChange={event => {
                        setSessionNumber(Number(event.target.value));
                        setSavedNotice(false);
                      }}
                      className="h-12 w-full rounded-xl bg-surface-container-high px-4 text-sm font-body font-medium text-on-surface outline-none transition-colors duration-150 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="session-title"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.sessionModal.labelTitle} />
                    </label>
                    <input
                      id="session-title"
                      type="text"
                      required
                      value={title}
                      onChange={event => {
                        setTitle(event.target.value);
                        setSavedNotice(false);
                      }}
                      placeholder={t(copy.sessionModal.placeholderTitle)}
                      className="h-12 w-full rounded-xl bg-surface-container-high px-4 text-sm font-body font-medium text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-[minmax(0,0.35fr)_minmax(0,1fr)]">
                  <div className="space-y-2">
                    <label
                      htmlFor="session-duration"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.sessionModal.labelDuration} />
                    </label>
                    <input
                      id="session-duration"
                      type="number"
                      min="1"
                      value={duration}
                      onChange={event => {
                        setDuration(Number(event.target.value));
                        setSavedNotice(false);
                      }}
                      placeholder={t(copy.sessionModal.placeholderDuration)}
                      className="h-12 w-full rounded-xl bg-surface-container-high px-4 text-sm font-body font-medium text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="session-desc"
                      className="block text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.sessionModal.labelDescription} />
                    </label>
                    <textarea
                      id="session-desc"
                      value={description}
                      onChange={event => {
                        setDescription(event.target.value);
                        setSavedNotice(false);
                      }}
                      placeholder={t(copy.sessionModal.placeholderDescription)}
                      className="min-h-28 w-full resize-y rounded-xl bg-surface-container-high px-4 py-3 text-sm font-body text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface-container-highest focus-visible:ring-2 focus-visible:ring-primary/20"
                    />
                  </div>
                </div>
              </section>
            )}

            {activeTab === 'LIVE' && (
              <section className="mx-auto max-w-3xl space-y-8">
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.sessionModal.stepTwo} />
                  </p>
                  <h3 className="mt-2 font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.sessionModal.liveHeading} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.sessionModal.liveDescription} />
                  </p>
                </div>
                <div className="rounded-2xl bg-surface-container-low p-5 sm:p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <label
                      htmlFor="session-meeting"
                      className="text-xs font-label font-bold text-on-surface"
                    >
                      <Bilingual {...copy.sessionModal.labelMeetingUrl} />
                    </label>
                    {meetingPlatform && (
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-label font-bold text-primary">
                        {meetingPlatform}
                      </span>
                    )}
                  </div>
                  <input
                    id="session-meeting"
                    type="url"
                    value={meetingUrl}
                    onChange={event => {
                      setMeetingUrl(event.target.value);
                      setSavedNotice(false);
                    }}
                    placeholder={t(copy.sessionModal.placeholderMeetingUrl)}
                    className="mt-3 h-12 w-full rounded-xl bg-surface-container-lowest px-4 text-sm font-body text-on-surface outline-none transition-colors duration-150 placeholder:text-on-surface-variant/60 hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary/20"
                  />
                  <p className="mt-2 min-h-[1lh] text-[11px] text-on-surface-variant">
                    <Bilingual {...copy.sessionModal.liveHelper} />
                  </p>
                  {meetingUrl.trim() && (
                    <a
                      href={meetingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex h-11 items-center gap-2 rounded-xl bg-surface-container-high px-4 text-xs font-label font-bold text-primary transition-colors duration-150 hover:bg-surface-container-highest focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                    >
                      <ExternalLink size={15} />
                      <Bilingual {...copy.sessionModal.testMeetingLink} />
                    </a>
                  )}
                </div>
              </section>
            )}

            {activeTab === 'MATERIALS' && (
              <section className="mx-auto max-w-3xl space-y-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                      <Bilingual {...copy.sessionModal.stepThree} />
                    </p>
                    <h3 className="mt-2 font-headline text-xl font-bold text-on-surface">
                      <Bilingual {...copy.sessionModal.materialsHeading} />
                    </h3>
                    <p className="mt-1 text-sm text-on-surface-variant">
                      <Bilingual {...copy.sessionModal.materialsDescription} />
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        if (managedSession)
                          void navigate('/evaluations/create', {
                            state: {
                              programId,
                              sessionId: managedSession.id,
                              sessionTitle: managedSession.title,
                              subjectName,
                            },
                          });
                      }}
                      disabled={!managedSession}
                      className="h-11 shrink-0 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                    >
                      <ClipboardCheck size={16} />
                      <Bilingual {...copy.learningPathStudio.addSessionEvaluation} />
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setEditingMaterial(undefined);
                        setIsMaterialEditorOpen(true);
                      }}
                      disabled={!managedSession}
                      className="h-11 shrink-0 rounded-xl px-4 text-xs font-label font-bold whitespace-nowrap active:scale-95"
                    >
                      <Plus size={16} />
                      <Bilingual {...copy.sessionModal.addMaterial} />
                    </Button>
                  </div>
                </div>
                {!managedSession ? (
                  <div className="rounded-2xl bg-surface-container-low p-6">
                    <p className="text-sm font-label font-bold text-on-surface">
                      <Bilingual {...copy.sessionModal.saveBeforeMaterialsTitle} />
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-on-surface-variant">
                      <Bilingual {...copy.sessionModal.saveBeforeMaterialsDescription} />
                    </p>
                  </div>
                ) : materials.length === 0 ? (
                  <div className="rounded-2xl bg-surface-container-low p-8 text-center">
                    <ClipboardCheck size={28} className="mx-auto text-secondary" />
                    <p className="mt-3 text-sm font-label font-bold text-on-surface">
                      <Bilingual {...copy.learningPathStudio.sessionEvaluationOnly} />
                    </p>
                    <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-on-surface-variant">
                      <Bilingual {...copy.learningPathStudio.sessionEvaluationOnlyDescription} />
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {materials.map(material => (
                      <div
                        key={material.id}
                        className="flex items-center gap-3 rounded-xl bg-surface-container-low p-4"
                      >
                        <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                          {material.type === 'VIDEO' ? <Video size={18} /> : <FileText size={18} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-label font-bold text-on-surface">
                            {material.order}. {material.title}
                          </p>
                          <p className="mt-0.5 text-[11px] font-label font-medium uppercase tracking-[0.12em] text-on-surface-variant">
                            {material.type} · {material.duration || 0}{' '}
                            {t(copy.sessionModal.minutesSuffix)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingMaterial(material);
                            setIsMaterialEditorOpen(true);
                          }}
                          className="grid size-11 shrink-0 place-items-center rounded-xl text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                          aria-label={`${t(copy.sessionModal.editMaterial)}: ${material.title}`}
                        >
                          <Pencil size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {activeTab === 'REVIEW' && (
              <section className="mx-auto max-w-3xl space-y-8">
                <div>
                  <p className="text-[10px] font-label font-bold uppercase tracking-[0.18em] text-primary">
                    <Bilingual {...copy.sessionModal.stepFour} />
                  </p>
                  <h3 className="mt-2 font-headline text-xl font-bold text-on-surface">
                    <Bilingual {...copy.sessionModal.reviewHeading} />
                  </h3>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    <Bilingual {...copy.sessionModal.reviewDescription} />
                  </p>
                </div>
                <div className="space-y-3">
                  {[
                    {
                      icon: <LayoutList size={18} />,
                      label: copy.sessionModal.reviewSession,
                      value: title || t(copy.sessionModal.emptyValue),
                    },
                    {
                      icon: <Clock size={18} />,
                      label: copy.sessionModal.labelDuration,
                      value: `${duration || 0} ${t(copy.sessionModal.minutesSuffix)}`,
                    },
                    {
                      icon: <Globe size={18} />,
                      label: copy.sessionModal.reviewLive,
                      value: meetingUrl || t(copy.sessionModal.notSet),
                    },
                    {
                      icon: <FileText size={18} />,
                      label: copy.sessionModal.reviewMaterials,
                      value: `${materials.length} ${t(copy.common.material)}`,
                    },
                  ].map(item => (
                    <div
                      key={item.label.id}
                      className="flex items-start gap-3 rounded-xl bg-surface-container-low p-4"
                    >
                      <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                        {item.icon}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-label font-bold uppercase tracking-[0.12em] text-on-surface-variant">
                          <Bilingual {...item.label} />
                        </p>
                        <p className="mt-1 break-all text-sm font-medium text-on-surface">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
          <footer className="flex flex-col-reverse gap-3 bg-surface-container-low px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <p className="text-xs leading-relaxed text-on-surface-variant">
              <Bilingual {...copy.sessionModal.saveHelper} />
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isPending}
                className="h-11 rounded-xl px-4 text-xs font-label font-bold text-on-surface-variant transition-colors duration-150 hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-55 active:scale-95"
              >
                <Bilingual {...copy.levelModal.abort} />
              </button>
              <Button
                variant="primary"
                type="submit"
                disabled={isPending || !title.trim()}
                className="h-11 rounded-xl px-5 text-xs font-label font-bold whitespace-nowrap active:scale-95"
              >
                {isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={16} />
                )}
                <Bilingual
                  {...(isPending
                    ? copy.sessionModal.btnSaving
                    : isPersisted
                      ? copy.sessionModal.btnSubmitEdit
                      : copy.sessionModal.btnSubmitCreate)}
                />
              </Button>
            </div>
          </footer>
        </form>
      </div>
      {isMaterialEditorOpen && managedSession && (
        <MaterialModal
          material={editingMaterial}
          flowId=""
          sessionId={managedSession.id}
          contextLabel={managedSession.title}
          initialOrder={materials.length + 1}
          programId={programId}
          onSaved={handleMaterialSaved}
          onDeleted={handleMaterialDeleted}
          onClose={() => setIsMaterialEditorOpen(false)}
        />
      )}
    </div>
  );
}
